/**
 * Input aggregation: keyboard, mouse (pointer-locked), and gamepad, exposed as
 * a stable per-frame snapshot so gameplay never touches raw DOM events.
 *
 * Edge queries (`pressed`, `released`) are valid only during the frame in which
 * the transition happened — read them in update(), not fixedUpdate().
 */

export const ACTIONS = {
  forward: ['KeyW', 'ArrowUp'],
  back: ['KeyS', 'ArrowDown'],
  left: ['KeyA', 'ArrowLeft'],
  right: ['KeyD', 'ArrowRight'],
  jump: ['Space'],
  crouch: ['ControlLeft', 'KeyC'],
  prone: ['KeyZ'],
  sprint: ['ShiftLeft'],
  reload: ['KeyR'],
  use: ['KeyF'],
  melee: ['KeyV'],
  leanLeft: ['KeyQ'],
  leanRight: ['KeyE'],
  swapWeapon: ['Digit1', 'Digit2', 'Tab'],
  grenade: ['KeyG'],
  flashlight: ['KeyT'],
  pause: ['Escape'],
};

/** W3C Standard Gamepad mapping, matching Fracture Protocol's control pad. */
const PAD_BUTTON_CODES = {
  0: 'Space',       // Cross: jump
  1: 'ControlLeft', // Circle: crouch
  2: 'KeyR',        // Square: reload
  3: 'KeyF',        // Triangle: use
  4: 'KeyV',        // L1: melee
  5: 'KeyG',        // R1: grenade
  9: 'Escape',      // Options: pause / resume
  10: 'ShiftLeft',  // L3: sprint
  11: 'Tab',        // R3: next weapon
  12: 'KeyT',       // D-pad up: flashlight
  13: 'KeyZ',       // D-pad down: prone
};

const PAD_L2 = 6;
const PAD_R2 = 7;
const PAD_DLEFT = 14;
const PAD_DRIGHT = 15;
const TRIGGER_ON = 0.55;
const TRIGGER_OFF = 0.35;
const RUMBLE_GAP_MS = 40;

export class Input {
  constructor(canvas, config, events = null) {
    this.canvas = canvas;
    this.config = config;
    this.events = events;

    this.down = new Set(); // codes currently held
    this._pressed = new Set(); // went down this frame
    this._released = new Set(); // went up this frame
    this._pendingDown = new Set();
    this._pendingUp = new Set();
    // Track device ownership so releasing a pad trigger cannot cancel a mouse
    // button (or a pad face button cancel the same keyboard key) still held.
    this._physicalDown = new Set();
    this._padDown = new Set();

    /** Accumulated pointer delta for this frame, in radians after sensitivity. */
    this.look = { x: 0, y: 0 };
    this._rawLook = { x: 0, y: 0 };
    this.wheel = 0;
    this._pendingWheel = 0;

    this.pointerLocked = false;
    this.enabled = true;
    /** Set true by capture mode so scripted shots aren't fought by real input. */
    this.frozen = false;

    this.gamepadIndex = null;
    this.stick = { moveX: 0, moveY: 0, lookX: 0, lookY: 0 };
    this.lastDevice = 'kbm';
    this._padPrev = [];
    this._padTrigger = { fire: false, ads: false };
    this._padCurve = { x: 0, y: 0 };
    this._rumbleAt = 0;

    this._bound = {
      keydown: this._onKeyDown.bind(this),
      keyup: this._onKeyUp.bind(this),
      mousedown: this._onMouseDown.bind(this),
      mouseup: this._onMouseUp.bind(this),
      mousemove: this._onMouseMove.bind(this),
      wheel: this._onWheel.bind(this),
      lockchange: this._onLockChange.bind(this),
      blur: this._onBlur.bind(this),
      contextmenu: (e) => e.preventDefault(),
      gamepadconnected: (e) => this._onGamepadConnected(e),
      gamepaddisconnected: (e) => this._onGamepadDisconnected(e),
    };

    this._rumbleOff = [];
    if (events) {
      this._rumbleOff.push(events.on('weapon:fire', () => this.rumble(0.3, 0.16, 55)));
      this._rumbleOff.push(events.on('damage:taken', ({ amount = 0 } = {}) => {
        const k = Math.min(1, 0.38 + amount / 70);
        this.rumble(k, k * 0.75, 210);
      }));
    }
  }

  attach() {
    addEventListener('keydown', this._bound.keydown);
    addEventListener('keyup', this._bound.keyup);
    addEventListener('mousedown', this._bound.mousedown);
    addEventListener('mouseup', this._bound.mouseup);
    addEventListener('mousemove', this._bound.mousemove);
    addEventListener('wheel', this._bound.wheel, { passive: true });
    addEventListener('blur', this._bound.blur);
    document.addEventListener('pointerlockchange', this._bound.lockchange);
    this.canvas.addEventListener('contextmenu', this._bound.contextmenu);
    addEventListener('gamepadconnected', this._bound.gamepadconnected);
    addEventListener('gamepaddisconnected', this._bound.gamepaddisconnected);
  }

  detach() {
    removeEventListener('keydown', this._bound.keydown);
    removeEventListener('keyup', this._bound.keyup);
    removeEventListener('mousedown', this._bound.mousedown);
    removeEventListener('mouseup', this._bound.mouseup);
    removeEventListener('mousemove', this._bound.mousemove);
    removeEventListener('wheel', this._bound.wheel);
    removeEventListener('blur', this._bound.blur);
    document.removeEventListener('pointerlockchange', this._bound.lockchange);
    this.canvas.removeEventListener('contextmenu', this._bound.contextmenu);
    removeEventListener('gamepadconnected', this._bound.gamepadconnected);
    removeEventListener('gamepaddisconnected', this._bound.gamepaddisconnected);
    this._releaseGamepad();
    for (const off of this._rumbleOff.splice(0)) off();
  }

  requestPointerLock() {
    // Chrome returns a promise that rejects if the document is not eligible
    // (headless capture, an iframe, a lock request too soon after an exit).
    // An unhandled rejection there shows up as a page error in the harness, so
    // swallow it: failing to lock is not a game error.
    try {
      const p = this.canvas.requestPointerLock?.();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    } catch {
      /* not eligible — keep running unlocked */
    }
  }

  _onKeyDown(e) {
    if (!this.enabled) return;
    if (e.repeat) return;
    // Let devtools/refresh through; swallow everything else the game binds.
    if (!e.metaKey && !e.ctrlKey) e.preventDefault();
    this.lastDevice = 'kbm';
    this._physicalDown.add(e.code);
    this._pendingDown.add(e.code);
  }

  _onKeyUp(e) {
    if (!this.enabled) return;
    this._physicalDown.delete(e.code);
    if (!this._padDown.has(e.code)) this._pendingUp.add(e.code);
  }

  _onMouseDown(e) {
    if (!this.enabled) return;
    if (!this.pointerLocked && e.button === 0) this.requestPointerLock();
    const code = `Mouse${e.button}`;
    this.lastDevice = 'kbm';
    this._physicalDown.add(code);
    this._pendingDown.add(code);
  }

  _onMouseUp(e) {
    if (!this.enabled) return;
    const code = `Mouse${e.button}`;
    this._physicalDown.delete(code);
    if (!this._padDown.has(code)) this._pendingUp.add(code);
  }

  _onMouseMove(e) {
    if (!this.enabled || !this.pointerLocked || this.frozen) return;
    this.lastDevice = 'kbm';
    // movementX/Y is already relative and unaffected by cursor clamping.
    this._rawLook.x += e.movementX ?? 0;
    this._rawLook.y += e.movementY ?? 0;
  }

  _onWheel(e) {
    if (!this.enabled) return;
    this.lastDevice = 'kbm';
    this._pendingWheel += Math.sign(e.deltaY);
  }

  _onLockChange() {
    this.pointerLocked = document.pointerLockElement === this.canvas;
    if (!this.pointerLocked) this._onBlur();
  }

  /** Losing focus must release every held key, or the player runs forever. */
  _onBlur() {
    for (const code of this._physicalDown) {
      if (!this._padDown.has(code)) this._pendingUp.add(code);
    }
    this._physicalDown.clear();
    this._rawLook.x = 0;
    this._rawLook.y = 0;
  }

  beginFrame() {
    this._pressed.clear();
    this._released.clear();

    // Poll before folding pending edges so controller presses are visible in
    // this frame, exactly like DOM keyboard and mouse events.
    this._pollGamepad();

    for (const code of this._pendingDown) {
      if (!this.down.has(code)) {
        this.down.add(code);
        this._pressed.add(code);
      }
    }
    for (const code of this._pendingUp) {
      if (this.down.delete(code)) this._released.add(code);
    }
    this._pendingDown.clear();
    this._pendingUp.clear();

    const s = this.config.sensitivity;
    this.look.x = this.frozen ? 0 : this._rawLook.x * s;
    this.look.y = this.frozen ? 0 : this._rawLook.y * s * (this.config.invertY ? -1 : 1);
    this._rawLook.x = 0;
    this._rawLook.y = 0;

    this.wheel = this._pendingWheel;
    this._pendingWheel = 0;

  }

  endFrame() {}

  _pollGamepad() {
    const pads = navigator.getGamepads?.() ?? [];
    const pad = pads[this.gamepadIndex ?? 0] ?? pads.find(Boolean);
    if (!pad) {
      this._releaseGamepad();
      return;
    }
    if (this.gamepadIndex !== pad.index) this._attachGamepad(pad);

    const live = this.enabled && !this.frozen;
    const buttons = pad.buttons ?? [];
    for (let i = 0; i < buttons.length; i++) {
      const on = !!(buttons[i]?.pressed || buttons[i]?.value > 0.5);
      const was = this._padPrev[i] === 1;
      this._padPrev[i] = on ? 1 : 0;
      if (on) this.lastDevice = 'pad';
      if (on === was) continue;
      const code = PAD_BUTTON_CODES[i];
      if (code) this._setPadCode(code, live && on);
      if (live && on && (i === PAD_DLEFT || i === PAD_DRIGHT)) {
        this._pendingWheel += i === PAD_DRIGHT ? 1 : -1;
      }
    }

    const r2 = buttons[PAD_R2]?.value ?? 0;
    const l2 = buttons[PAD_L2]?.value ?? 0;
    const fire = live && (this._padTrigger.fire ? r2 > TRIGGER_OFF : r2 > TRIGGER_ON);
    const ads = live && (this._padTrigger.ads ? l2 > TRIGGER_OFF : l2 > TRIGGER_ON);
    if (fire !== this._padTrigger.fire) {
      this._padTrigger.fire = fire;
      this._setPadCode('Mouse0', fire);
    }
    if (ads !== this._padTrigger.ads) {
      this._padTrigger.ads = ads;
      this._setPadCode('Mouse2', ads);
    }

    if (!live) {
      this.stick.moveX = this.stick.moveY = this.stick.lookX = this.stick.lookY = 0;
      return;
    }
    const axes = pad.axes ?? [];
    const dz = Math.min(0.35, Math.max(0.05, this.config.padDeadzone ?? 0.14));
    const move = this._curveStick(axes[0] ?? 0, axes[1] ?? 0, dz, 1);
    this.stick.moveX = move.x;
    this.stick.moveY = move.y;
    const look = this._curveStick(axes[2] ?? 0, axes[3] ?? 0, dz, 2);
    this.stick.lookX = look.x;
    this.stick.lookY = look.y;
    if (move.x || move.y || look.x || look.y) this.lastDevice = 'pad';
  }

  _attachGamepad(pad) {
    this.gamepadIndex = pad.index;
    this._padPrev.length = 0;
    console.info(`[gamepad] ${pad.id || 'unknown device'} — mapping: ${pad.mapping || 'non-standard'}`);
  }

  _onGamepadConnected(e) {
    if (this.gamepadIndex === null && e.gamepad) this._attachGamepad(e.gamepad);
  }

  _onGamepadDisconnected(e) {
    if (e.gamepad?.index !== this.gamepadIndex) return;
    this._releaseGamepad();
    this.gamepadIndex = null;
  }

  _setPadCode(code, on) {
    if (on) {
      if (this._padDown.has(code)) return;
      this._padDown.add(code);
      if (!this.down.has(code)) this._pendingDown.add(code);
    } else {
      if (!this._padDown.delete(code)) return;
      if (!this._physicalDown.has(code)) this._pendingUp.add(code);
    }
  }

  _releaseGamepad() {
    for (const code of [...this._padDown]) this._setPadCode(code, false);
    this._padPrev.length = 0;
    this._padTrigger.fire = this._padTrigger.ads = false;
    this.stick.moveX = this.stick.moveY = this.stick.lookX = this.stick.lookY = 0;
  }

  /** Circular dead zone re-scaled from its edge, then Fracture's aim curve. */
  _curveStick(x, y, deadzone, exponent) {
    const mag = Math.hypot(x, y);
    const out = this._padCurve;
    if (mag <= deadzone) {
      out.x = out.y = 0;
      return out;
    }
    const n = Math.min(1, (mag - deadzone) / (1 - deadzone));
    const k = (exponent === 1 ? n : n ** exponent) / mag;
    out.x = x * k;
    out.y = y * k;
    return out;
  }

  get gamepadConnected() {
    const pads = navigator.getGamepads?.() ?? [];
    return this.gamepadIndex !== null && !!pads[this.gamepadIndex]?.connected;
  }

  rumble(strong, weak, duration) {
    if (!this.gamepadConnected || this.config.rumble === false) return;
    const now = performance.now();
    if (now - this._rumbleAt < RUMBLE_GAP_MS) return;
    this._rumbleAt = now;
    try {
      const pad = navigator.getGamepads()[this.gamepadIndex];
      pad?.vibrationActuator?.playEffect?.('dual-rumble', {
        startDelay: 0,
        duration,
        weakMagnitude: Math.min(1, weak),
        strongMagnitude: Math.min(1, strong),
      })?.catch?.(() => {});
    } catch {
      /* Unsupported actuator or browser — controls must keep working. */
    }
  }

  /** True while any key bound to `action` is held. */
  action(name) {
    const codes = ACTIONS[name];
    if (!codes) return false;
    for (const c of codes) if (this.down.has(c)) return true;
    return false;
  }

  actionPressed(name) {
    const codes = ACTIONS[name];
    if (!codes) return false;
    for (const c of codes) if (this._pressed.has(c)) return true;
    return false;
  }

  held(code) {
    return this.down.has(code);
  }

  pressed(code) {
    return this._pressed.has(code);
  }

  released(code) {
    return this._released.has(code);
  }

  get fire() {
    return this.down.has('Mouse0');
  }

  get firePressed() {
    return this._pressed.has('Mouse0');
  }

  get ads() {
    return this.down.has('Mouse2');
  }

  /** Normalised WASD + left-stick movement, clamped to the unit disc so
   *  diagonals aren't faster than cardinals. */
  moveVector(out = { x: 0, y: 0 }) {
    let x = (this.action('right') ? 1 : 0) - (this.action('left') ? 1 : 0);
    let y = (this.action('forward') ? 1 : 0) - (this.action('back') ? 1 : 0);
    x += this.stick.moveX;
    y -= this.stick.moveY;
    const len = Math.hypot(x, y);
    if (len > 1) {
      x /= len;
      y /= len;
    }
    out.x = x;
    out.y = y;
    return out;
  }
}
